import { useEffect, useState, type CSSProperties } from 'react';
import {
  faArrowRight,
  faChevronDown,
  faChevronRight,
  faCircleExclamation,
  faSparkles,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '../../../core';
import { SurfaceHeader, type SurfacePadding } from '../SurfaceHeader';
import styles from './UnderwriterHandoff.module.css';
import {
  followUpChangeSet,
  handoffState,
  resultArrived,
  type CardView,
  type HandoffAlternative,
  type PendingFollowUp,
  type HandoffFollowUp,
  type HandoffRefusedChange,
  type HandoffState,
} from './handoffState';

/** The card's padding. Its header band, change table, total and footnote all
 *  run edge to edge by cancelling exactly this, through `--card-pad`. */
const CARD_PADDING: SurfacePadding = 'md';

/** How many rows stay visible before the expander. */
const VISIBLE_ROWS = 3;

export interface HandoffRow {
  readonly id: string;
  /** The coverage, modifier or global, as the quote grid names it. */
  readonly rowLabel: string;
  /** The rater category, e.g. "Third Party Expense". Absent while the rater
   *  response has not arrived, rather than guessed at. */
  readonly sectionTitle?: string;
  /** The edited field, e.g. "Retention". Absent when the row IS the field. */
  readonly fieldLabel?: string;
  /** The suite part the row belongs to, e.g. "Directors and Officers". Absent
   *  on a quote that is not made of parts. Keep the name in step with
   *  `DescribedRow` in the console. */
  readonly partLabel?: string;
  readonly fromLabel: string;
  readonly toLabel: string;
  /**
   * Signed premium impact for this row. Absent when the change owns no row
   * premium — a rater option reprices everything and belongs to nothing — and
   * the cell then reads "rerate" rather than inventing an attributed share.
   */
  readonly premiumDelta?: number;
  /**
   * Which coverage this row's premium belongs to. Rows sharing one carry the
   * same figure, and the card prints it on the first of them only.
   */
  readonly premiumGroupId?: string;
}

/**
 * Everything the console writes for this card, as ONE object.
 *
 * Bound to `/results/<componentId>` as a whole, not as seven separate deep
 * paths. The deep bindings did not pick up the parent-object write when it
 * first arrived — the data sat in the model while the card rendered `undefined`
 * — and the two surfaces that have always worked, CopyQuote and EditQuote,
 * each bind a single object path. Measured in the browser: writing the deep
 * paths by hand woke the card up immediately.
 */
export interface HandoffResult {
  readonly status?: string;
  readonly rows?: readonly HandoffRow[];
  /** Changes the quote would not take, each with why and what it would. */
  readonly refused?: readonly HandoffRefusedChange[];
  /** Where a "Use $25,000" button addresses its change-set. */
  readonly followUp?: HandoffFollowUp;
  readonly accountName?: string | null;
  readonly quoteNumber?: string | null;
  readonly netPremiumDelta?: number;
  /**
   * Referral reasons the rater gives the quote because of this change and did
   * not give it before. Explains a price that did not move; blocks nothing.
   * Same name as the console's `HandoffSnapshot` field: JSON crosses between
   * them.
   */
  readonly referralReasons?: readonly string[];
  readonly currency?: string;
  readonly locale?: string;
  readonly historical?: boolean;
  /** Set by the host when it failed rather than answered. */
  readonly error?: boolean;
  /** Why the host refused or failed, for the "Couldn't add" state. */
  readonly message?: string;
  /** Why the last "Use $25,000" could not be added. The card is otherwise as it was. */
  readonly followUpError?: string;
}

export interface UnderwriterHandoffProps {
  /** Written by the console to `/results/<componentId>`. */
  readonly result?: HandoffResult;
  readonly rows?: readonly HandoffRow[];
  readonly accountName?: string;
  readonly quoteNumber?: string;
  /** ISO 4217, matching whatever the quote is priced in. */
  /**
   * The whole quote's premium movement, from the console's own rate.
   *
   * NOT the sum of the rows: a global or a rater option reprices every line and
   * owns no row premium, so summing row deltas reports only the fraction that
   * happened to be attributable. Observed staging a 3M→2M aggregate limit, a
   * retention change and one coverage removal, where the row sum read −$113.98
   * — the coverage alone — while the quote itself had moved by far more.
   */
  readonly netPremiumDelta?: number;
  readonly currency?: string;
  readonly locale?: string;
  /**
   * Fired ONCE on mount, by this component rather than by the underwriter.
   *
   * There is no Apply on this card by design, but something still has to tell
   * the console to stage the change-set — and the change-set lives in the
   * action binding the agent supplies, which the binder hands us as an opaque
   * `() => void`. So the agent binds it and the card fires it, the same way
   * `ViewChangeProposal` fires its preview.
   *
   * Unbound, the card degrades to a receipt for something that never staged,
   * so it says so rather than claiming "In workspace".
   */
  readonly onStage?: () => void;
  /**
   * This change-set was staged in an earlier session and is not staged now.
   *
   * A reopened conversation replays this card, which fires its staging action
   * on mount exactly like a fresh one. The host refuses to stage it a second
   * time, and the card has to say what is true rather than sit on "Staging on
   * the workspace…" for a change that is not there.
   */
  readonly historical?: boolean;
  /** Sends a "Use $25,000" change-set. Without it the buttons are omitted. */
  readonly onFollowUp?: (changeSet: Record<string, unknown>) => void;
}

const formatMoney = (value: number, currency: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

/** Signed, e.g. "−$586.06" / "+$120.00". Uses a real minus sign, not a hyphen. */
const formatDelta = (value: number, currency: string, locale: string) => {
  if (Math.abs(value) < 0.005) {
    return formatMoney(0, currency, locale);
  }
  const sign = value > 0 ? '+' : '−';
  return `${sign}${formatMoney(Math.abs(value), currency, locale)}`;
};

/**
 * The net line: "No change" for a movement that shows as zero, the signed
 * amount otherwise. Same threshold as `deltaColour`, so a figure never reads
 * "$0.00" in the dimmed colour of "no movement".
 *
 * The words match the console's banner (`NO_CHANGE_LABEL` in
 * `proposedChangesModel.ts`); this lib cannot import the console, so the string
 * is kept here and the two are changed together. Rows keep `formatDelta`: a row
 * that owns no premium reads "rerate", and a zero row is a figure, not a verdict.
 */
export const formatNetDelta = (
  value: number,
  currency: string,
  locale: string
): string =>
  Math.abs(value) < 0.005 ? 'No change' : formatDelta(value, currency, locale);

const deltaColour = (delta: number | undefined) => {
  if (delta === undefined || Math.abs(delta) < 0.005) {
    return 'dimmed';
  }
  return delta > 0 ? 'red.6' : 'teal.6';
};

/**
 * A premium belongs to a coverage, not to a field, so the second row of a
 * two-field coverage says where its figure went rather than repeating it.
 *
 * Decided HERE, over the rows this card actually received, rather than by the
 * producer over the whole draft. The producer cannot know what was filtered
 * out: when the underwriter had already touched the same coverage, THEIR row
 * held the figure, was dropped from this card, and the remaining row pointed
 * at a row nobody could see.
 *
 * `rerate` is a different claim: the change reprices every line and owns no row
 * premium at all.
 */
const renderRowPremium = (
  row: HandoffRow,
  rows: readonly HandoffRow[],
  currency: string,
  locale: string
): string => {
  if (row.premiumDelta === undefined) {
    return 'rerate';
  }
  const owner = row.premiumGroupId
    ? rows.find((candidate) => candidate.premiumGroupId === row.premiumGroupId)
    : row;
  if (owner && owner.id !== row.id) {
    return 'incl. above';
  }
  return formatDelta(row.premiumDelta, currency, locale);
};

interface StateCopy {
  readonly badge: string;
  readonly tone: string;
}

const STATE_COPY: Record<HandoffState, StateCopy> = {
  checking: { badge: 'Checking', tone: 'blue' },
  ready: { badge: 'Ready to review', tone: 'green' },
  partial: { badge: 'Partly added', tone: 'yellow' },
  'not-added': { badge: 'Not added', tone: 'gray' },
  blocked: { badge: 'Couldn’t add', tone: 'red' },
  unconfirmed: { badge: 'Not confirmed', tone: 'red' },
  historical: { badge: 'Suggested earlier', tone: 'gray' },
  unbound: { badge: 'Not added', tone: 'gray' },
};

const changes = (count: number) => (count === 1 ? 'change' : 'changes');

interface SummaryInputs {
  readonly state: HandoffState;
  readonly added: number;
  readonly refused: number;
  readonly accountName?: string | null;
}

/** The line under the title: what happened to the quote, in plain words. */
function summaryLine({
  state,
  added,
  refused,
  accountName,
}: SummaryInputs): string {
  const onAccount = accountName ? ` to ${accountName}` : '';
  switch (state) {
    case 'checking':
      return 'Adding these changes to your quote…';
    case 'ready':
      return `${added} ${changes(added)} added${onAccount}`;
    case 'partial':
      return `${added} of ${added + refused} changes added${onAccount}`;
    case 'not-added':
      return refused === 1
        ? 'That change couldn’t be added'
        : `None of the ${refused} changes could be added`;
    case 'blocked':
      return 'This suggestion couldn’t be added';
    case 'unconfirmed':
      return 'We couldn’t confirm these changes';
    case 'historical':
      return 'These changes were suggested earlier';
    default:
      return 'Nothing was added';
  }
}

/** The closing line: what the underwriter does next, and that nothing saved. */
function footnoteFor(state: HandoffState): string | undefined {
  switch (state) {
    case 'checking':
      return 'Nothing is saved until you accept it on the quote.';
    case 'ready':
      return 'Accept or reject each change on the quote. Nothing is saved until you do.';
    case 'partial':
      return 'Review the added changes on the quote. Nothing else changed.';
    case 'not-added':
    case 'blocked':
      return 'The quote is unchanged.';
    case 'unconfirmed':
      return 'Nothing was saved.';
    case 'historical':
      return 'They aren’t on the quote now.';
    default:
      return undefined;
  }
}

/** A fresh change-set id for a follow-up, without a dependency for it. */
function newChangeSetId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `cs-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Column labels, in the same grid as ChangeLine. The first column carries
 *  the summary ("7 changes added to …"), which names what the rows are. */
function TableHeader({ summary }: Readonly<{ summary: string }>) {
  return (
    <div className={styles.headRow}>
      <span>{summary}</span>
      <span />
      <span className={styles.headPremium}>Premium</span>
    </div>
  );
}

/** The line under a row's name: which field, which part, which category. */
export function rowContext(row: HandoffRow): string {
  return [row.fieldLabel, row.partLabel, row.sectionTitle]
    .filter(Boolean)
    .join(' · ');
}

/** One change as a single dense, non-interactive line. */
function ChangeLine({
  row,
  rows,
  currency,
  locale,
}: {
  readonly row: HandoffRow;
  /** Every row on this card, for deciding which one owns a shared premium. */
  readonly rows: readonly HandoffRow[];
  readonly currency: string;
  readonly locale: string;
}) {
  const sectionLine = rowContext(row);

  return (
    <div className={styles.row}>
      <div className={styles.field}>
        <div className={styles.fieldName}>{row.rowLabel}</div>
        {sectionLine ? (
          <div className={styles.fieldSection}>{sectionLine}</div>
        ) : null}
      </div>

      <span className={styles.change}>
        <span className={styles.from}>{row.fromLabel}</span>
        <span className={styles.arrow} aria-hidden>
          →
        </span>
        <span className={styles.to}>{row.toLabel}</span>
      </span>

      <Text
        component="span"
        className={styles.premium}
        c={deltaColour(row.premiumDelta)}
      >
        {renderRowPremium(row, rows, currency, locale)}
      </Text>
    </div>
  );
}

/** One refused change: what was asked, why not, and what would work. */
function RefusedLine({
  change,
  onUse,
  disabled,
  adding,
}: Readonly<{
  change: HandoffRefusedChange;
  onUse?: (alternative: HandoffAlternative) => void;
  disabled: boolean;
  /** The value a follow-up is adding for this change, while it is in flight. */
  adding?: string;
}>) {
  const name = change.fieldLabel
    ? `${change.label} · ${change.fieldLabel}`
    : change.label;
  const alternatives = onUse ? change.alternatives ?? [] : [];
  return (
    <Stack gap={4}>
      <Group gap={4} wrap="wrap">
        <Text size="xs" fw={700}>
          {name}
        </Text>
        <FontAwesomeIcon icon={faArrowRight} fontSize={11} />
        <Text size="xs" fw={700}>
          {change.toLabel}
        </Text>
      </Group>
      <Text size="xs">{change.message}</Text>
      {adding ? (
        <Group gap="xs" wrap="nowrap">
          <Loader size="xs" />
          <Text size="xs">Adding {adding}…</Text>
        </Group>
      ) : null}
      {!adding && alternatives.length > 0 ? (
        <Group gap={6} wrap="wrap">
          {alternatives.map((alternative) => (
            <Button
              key={alternative.label}
              size="compact-xs"
              variant="light"
              disabled={disabled}
              onClick={() => onUse?.(alternative)}
            >
              Use {alternative.label}
            </Button>
          ))}
        </Group>
      ) : null}
    </Stack>
  );
}

function RefusedList({
  refused,
  onUse,
  pending,
  followUpError,
}: Readonly<{
  refused: readonly HandoffRefusedChange[];
  onUse?: (
    change: HandoffRefusedChange,
    alternative: HandoffAlternative
  ) => void;
  pending: PendingFollowUp | null;
  followUpError?: string;
}>) {
  return (
    <Stack
      gap="xs"
      mt="sm"
      p="sm"
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        background: 'var(--mantine-color-yellow-0)',
        border: '1px solid var(--mantine-color-yellow-3)',
      }}
    >
      <Group gap={6} wrap="nowrap">
        <FontAwesomeIcon icon={faTriangleExclamation} fontSize={12} />
        <Text size="xs" fw={700} tt="uppercase">
          Couldn’t add
        </Text>
      </Group>
      {refused.map((change) => (
        <RefusedLine
          key={change.id}
          change={change}
          onUse={
            onUse ? (alternative) => onUse(change, alternative) : undefined
          }
          disabled={pending !== null}
          adding={pending?.id === change.id ? pending.label : undefined}
        />
      ))}
      {followUpError && pending === null ? (
        <Text size="xs" fw={600}>
          {followUpError}
        </Text>
      ) : null}
    </Stack>
  );
}

/**
 * Why the price may not have moved: the rater refers the quote to underwriting
 * because of this change. It explains and blocks nothing, so it reads as a
 * note, not a warning.
 */
function ReferralNotice({ reasons }: Readonly<{ reasons: readonly string[] }>) {
  return (
    <div className={styles.referral}>
      <Group gap="xs" wrap="nowrap">
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <Text size="xs" fw={700}>
          Refers to underwriting:
        </Text>
      </Group>
      <ul className={styles.referralList}>
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

/** The whole proposal was refused or the host failed: one reason, no rows. */
function BlockedNotice({
  state,
  message,
}: Readonly<{ state: HandoffState; message?: string }>) {
  const fallback =
    state === 'unconfirmed'
      ? 'Check the quote for highlighted changes, or ask Moo to try again.'
      : 'Ask Moo for a new suggestion based on the quote as it is now.';
  return (
    <Group
      gap="xs"
      wrap="nowrap"
      align="flex-start"
      mt="sm"
      p="sm"
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        background: 'var(--mantine-color-red-0)',
        border: '1px solid var(--mantine-color-red-3)',
      }}
    >
      <FontAwesomeIcon icon={faCircleExclamation} fontSize={13} />
      <Text size="xs">
        {state === 'blocked' && message ? message : fallback}
      </Text>
    </Group>
  );
}

/**
 * Receipt for changes the agent suggested on the underwriter's quote.
 *
 * Shows the check while it runs, then what was added and what wasn't. The
 * decision on each added change lives on the workspace beside the affected
 * field, not on a chat card that would be a second place to decide the same
 * thing — so the only controls here are for a refused change: using the
 * closest value the quote does take. The card records what was sent, and goes
 * out of date the moment the underwriter resolves anything, which is expected
 * of a transcript.
 */
export function UnderwriterHandoff({
  result: resultProp,
  rows: rowsProp,
  accountName: accountNameProp,
  quoteNumber: quoteNumberProp,
  netPremiumDelta: netPremiumDeltaProp,
  currency: currencyProp,
  locale: localeProp,
  onStage,
  historical: historicalProp,
  onFollowUp,
}: UnderwriterHandoffProps) {
  const [view, setView] = useState<CardView<HandoffResult>>({
    shown: resultProp,
    pending: null,
  });
  useEffect(() => {
    setView((current) => resultArrived(current, resultProp));
  }, [resultProp]);
  const result = view.shown;
  const { pending } = view;

  // `result` is what the A2UI binding supplies; the individual props are what
  // stories and tests pass. One object wins when present so the two cannot
  // describe the card differently.
  const rows = result?.rows ?? rowsProp ?? [];
  const refused = result?.refused ?? [];
  const referralReasons = result?.referralReasons ?? [];
  const accountName = result?.accountName ?? accountNameProp;
  const quoteNumber = result?.quoteNumber ?? quoteNumberProp;
  const netPremiumDelta = result?.netPremiumDelta ?? netPremiumDeltaProp;
  const currency = result?.currency ?? currencyProp ?? 'USD';
  const locale = result?.locale ?? localeProp ?? 'en-US';
  const historical = (result?.historical ?? historicalProp) === true;
  const [expanded, setExpanded] = useState(false);
  // State, not a ref: refs are for DOM nodes here, and this is a latch that
  // must survive the re-render its own dispatch causes.
  const [requested, setRequested] = useState(false);
  useEffect(() => {
    // A result already present means this card is being replayed onto a
    // rebuilt processor, not mounted fresh. Re-firing would re-stage a
    // change-set the underwriter may already have accepted or rejected — a far
    // worse outcome than the wasted rater call the equivalent preview guard
    // prevents.
    if (!onStage || requested || resultProp || rows.length > 0 || historical) {
      return;
    }
    setRequested(true);
    onStage();
  }, [onStage, requested, resultProp, rows.length, historical]);

  const state = handoffState({
    result,
    rows: rowsProp,
    historical: historicalProp,
    bound: Boolean(onStage),
  });
  const copy = STATE_COPY[state];
  const footnote = footnoteFor(state);

  const useAlternative = onFollowUp
    ? (change: HandoffRefusedChange, alternative: HandoffAlternative) => {
        const changeSetId = newChangeSetId();
        const changeSet = followUpChangeSet(
          result ?? {},
          change.id,
          alternative,
          changeSetId
        );
        if (!changeSet) {
          return;
        }
        setView((current) => ({
          ...current,
          pending: { id: change.id, label: alternative.label, changeSetId },
        }));
        onFollowUp(changeSet);
      }
    : undefined;

  const hiddenCount = Math.max(rows.length - VISIBLE_ROWS, 0);
  const visibleRows = expanded ? rows : rows.slice(0, VISIBLE_ROWS);
  const summary = summaryLine({
    state,
    added: rows.length,
    refused: refused.length,
    accountName,
  });
  // With rows, the summary heads the table's first column instead.
  const sublineSummary = rows.length > 0 ? null : summary;

  return (
    <Paper
      radius="md"
      p={CARD_PADDING}
      withBorder
      className={styles.card}
      style={
        {
          width: '100%',
          '--card-pad': `var(--mantine-spacing-${CARD_PADDING})`,
        } as CSSProperties
      }
    >
      <SurfaceHeader padding={CARD_PADDING}>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <div className={styles.title}>
              <FontAwesomeIcon icon={faSparkles} className={styles.titleMark} />
              <span>Proposed changes</span>
            </div>
            {sublineSummary || quoteNumber ? (
              <div className={styles.subline}>
                {sublineSummary}
                {sublineSummary && quoteNumber ? ' · ' : null}
                {quoteNumber ? (
                  <span className={styles.quoteRef}>Quote {quoteNumber}</span>
                ) : null}
              </div>
            ) : null}
          </div>
          <StatusPill label={copy.badge} tone={copy.tone} />
        </div>
      </SurfaceHeader>

      {state === 'checking' ? (
        <Group gap="xs" mt="sm" wrap="nowrap">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Checking each change against the quote…
          </Text>
        </Group>
      ) : null}

      {rows.length > 0 ? (
        <div className={styles.table}>
          <TableHeader summary={summary} />
          {visibleRows.map((row) => (
            <ChangeLine
              key={row.id}
              row={row}
              rows={rows}
              currency={currency}
              locale={locale}
            />
          ))}
        </div>
      ) : null}

      {hiddenCount > 0 ? (
        <UnstyledButton
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className={styles.more}
        >
          <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </UnstyledButton>
      ) : null}

      {refused.length > 0 ? (
        <RefusedList
          refused={refused}
          onUse={useAlternative}
          pending={pending}
          followUpError={result?.followUpError}
        />
      ) : null}

      {state === 'blocked' || state === 'unconfirmed' ? (
        <BlockedNotice state={state} message={result?.message} />
      ) : null}

      {rows.length > 0 ? (
        <div className={styles.total}>
          <div>
            <div className={styles.totalLabel}>Net premium impact</div>
            <div className={styles.totalHint}>
              {rows.length === 1
                ? 'If you accept this change'
                : `If you accept all ${rows.length} changes`}
            </div>
          </div>
          <Text
            component="span"
            className={styles.totalAmount}
            c={
              netPremiumDelta === undefined
                ? 'dimmed'
                : deltaColour(netPremiumDelta)
            }
          >
            {netPremiumDelta === undefined
              ? 'pending'
              : formatNetDelta(netPremiumDelta, currency, locale)}
          </Text>
        </div>
      ) : null}

      {rows.length > 0 && referralReasons.length > 0 ? (
        <ReferralNotice reasons={referralReasons} />
      ) : null}

      {footnote ? <div className={styles.footnote}>{footnote}</div> : null}
    </Paper>
  );
}

/** The card's state as a coloured dot and a sentence-case label. */
function StatusPill({
  label,
  tone,
}: Readonly<{ label: string; tone: string }>) {
  return (
    <span
      className={styles.status}
      style={
        {
          '--status-tone': `var(--mantine-color-${tone}-6)`,
          '--status-ink': `var(--mantine-color-${tone}-8)`,
          '--status-ink-dark': `var(--mantine-color-${tone}-3)`,
        } as CSSProperties
      }
    >
      <span className={styles.statusDot} aria-hidden />
      {label}
    </span>
  );
}
